import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { TASK_TYPE_LABELS } from './constants';
import DependencyPicker from './DependencyPicker';

const empty = {
  title: '',
  description: '',
  task_type: 'human',
  status: 'todo',
  phase: '',
  estimate_hours: '',
  motivo_de_reestimacion: '',
  asignacion_pct: '',
  planned_start: '',
  planned_end: '',
  assignee_kind: '',
  assignee_id: '',
  assignee_name: '',
};

export default function TaskDialog({
  open,
  onOpenChange,
  task,
  phases,
  taskStatuses,
  teamMembers,
  agents,
  onSave,
  saving,
  horario,
  tenantId,
  capsuleId,
  ticketId,
  incomingLinks,
  /** Las tareas del caso, que el tablero ya tiene cargadas: la lista no cuesta una request. */
  tareasDelCaso = [],
}) {
  const [form, setForm] = useState(empty);
  const [predecessors, setPredecessors] = useState([]);
  const isEdit = Boolean(task?.task_id);

  useEffect(() => {
    if (!open) return;
    if (task) {
      const a = task.assignee || {};
      setForm({
        title: task.title || '',
        description: task.description || '',
        task_type: task.task_type || 'human',
        status: task.status || 'todo',
        phase: task.phase || '',
        estimate_hours: task.estimate_hours != null ? String(task.estimate_hours) : '',
        // Vacio al abrir: el motivo es de **este** cambio, no del anterior. El historial de
        // re-estimaciones se lee en la ficha de la tarea.
        motivo_de_reestimacion: '',
        // Se poblaba desde `empty` y nunca desde la tarea, asi que abrir una con 6 % mostraba
        // el campo vacio con el placeholder en 100: el formulario decia otra cosa que el plan.
        asignacion_pct: task.asignacion_pct != null ? String(task.asignacion_pct) : '',
        planned_start: task.planned_start || '',
        planned_end: task.planned_end || '',
        assignee_kind: a.kind || '',
        assignee_id: a.id || a.agent_id || '',
        assignee_name: a.name || '',
      });
      const preds = (incomingLinks || [])
        .filter((l) => l.to_kind === 'task' && l.to_id === task.task_id)
        .map((l) => ({
          kind: l.from_kind,
          id: l.from_id,
          label: l.from_label || `${l.from_kind}:${l.from_id}`,
          link_id: l.link_id,
          // **El tipo y la espera viajaban y se tiraban aca.** Sin esto el selector los muestra
          // en su valor por omision, y guardar sin tocar nada degradaria a fin-a-comienzo un
          // vinculo que alguien eligio de otro tipo. Un formulario que no lee lo que hay
          // sobrescribe con lo que supone.
          link_type: l.link_type,
          lag_dias: l.lag_dias,
        }));
      setPredecessors(preds);
    } else {
      setForm(empty);
      setPredecessors([]);
    }
  }, [open, task, incomingLinks]);

  const buildAssignee = () => {
    if (!form.assignee_kind) return null;
    if (form.assignee_kind === 'agent') {
      const id = form.assignee_id;
      const ag = agents.find((x) => x.agent_id === id);
      return { kind: 'agent', id, agent_id: id, name: ag?.name || form.assignee_name || id };
    }
    const m = teamMembers.find((x) => x.user_email === form.assignee_id);
    return {
      kind: 'human',
      id: form.assignee_id,
      name: m?.full_name || form.assignee_name || form.assignee_id,
    };
  };

  /**
   * Cuantos dias habiles sale esa tarea con esa dedicacion. Es la misma cuenta que hace el
   * backend (`services/task_schedule.dias_de_trabajo`) y la misma que aplica el Gantt al
   * estirar una barra: tres lugares con el mismo numero, o el formulario prometeria un plan
   * distinto del que se guarda.
   */
  const duracionQueResulta = useMemo(() => {
    const horas = parseFloat(form.estimate_hours);
    if (!horas || horas <= 0) return 'Sin estimacion, la barra usa el default de 8 h.';
    const desde = String(horario?.desde || '08:00').split(':').map(Number);
    const hasta = String(horario?.hasta || '17:00').split(':').map(Number);
    const jornada = Math.max(0, (hasta[0] * 60 + (hasta[1] || 0)) - (desde[0] * 60 + (desde[1] || 0))) / 60;
    const pct = Math.min(100, Math.max(1, parseInt(form.asignacion_pct, 10) || 100));
    if (!jornada) return `${horas} h · el horario del area no declara jornada.`;
    const dias = Math.max(1, Math.ceil(horas / (jornada * (pct / 100))));
    return `${horas} h al ${pct} %, con jornada de ${jornada} h, son ${dias} dia(s) habil(es).`;
  }, [form.estimate_hours, form.asignacion_pct, horario]);

  /**
   * Si esto es **re-estimar** y no estimar por primera vez.
   *
   * La distincion importa: lo que hay que registrar es el cambio de un numero sobre el que alguien
   * ya planifico. Sin el motivo, una tarea que consumio 8 de 8 h con el 20 % declarado se «arregla»
   * subiendo el estimado a 40: la alerta de desvio se apaga y no queda nada que diga que la
   * estimacion original estaba mal.
   */
  const estimadoAnterior = task?.estimate_hours;
  const reestimando = Boolean(
    isEdit
    && estimadoAnterior !== null && estimadoAnterior !== undefined && estimadoAnterior !== ''
    && form.estimate_hours !== ''
    && parseFloat(form.estimate_hours) !== parseFloat(estimadoAnterior),
  );

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    if (reestimando && !form.motivo_de_reestimacion.trim()) return;
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      task_type: form.task_type,
      phase: form.phase || null,
      estimate_hours: form.estimate_hours ? parseFloat(form.estimate_hours) : null,
      // **El motivo solo viaja cuando es una re-estimacion de verdad.** El backend lo exige para
      // cambiar un estimado que ya tenia valor, y lo ignora al estimar por primera vez. Mandarlo
      // siempre pediria explicar algo que no paso.
      ...(reestimando ? { motivo_de_reestimacion: form.motivo_de_reestimacion.trim() } : {}),
      // Vacio **es** 100, que es lo que promete el placeholder. Mandar `null` lo dejaba
      // afuera del PATCH (`exclude_none`) y el campo quedaba con el valor viejo: el
      // formulario prometia una cosa y guardaba otra.
      asignacion_pct: Math.min(100, Math.max(1, parseInt(form.asignacion_pct, 10) || 100)),
      planned_start: form.planned_start || null,
      planned_end: form.planned_end || null,
      dates_confirmed: Boolean(form.planned_start || form.planned_end),
      assignee: buildAssignee(),
      input_payload: {},
    };
    if (isEdit) payload.status = form.status;
    else payload.status = 'todo';
    onSave(payload, task?.task_id, { predecessors });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm border-brand-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{isEdit ? 'Editar tarea' : 'Nueva tarea'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-xs">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Titulo *</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="h-8 rounded-sm mt-0.5" data-testid="task-title-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Descripcion</Label>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="rounded-sm mt-0.5 min-h-[160px] text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Tipo</Label>
              <select value={form.task_type} onChange={(e) => setForm((f) => ({ ...f, task_type: e.target.value }))} className="w-full border border-brand-border rounded-sm h-8 px-2 mt-0.5 bg-white">
                {Object.entries(TASK_TYPE_LABELS).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
              </select>
            </div>
            {isEdit && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Estado</Label>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full border border-brand-border rounded-sm h-8 px-2 mt-0.5 bg-white">
                  {(taskStatuses || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            )}
          </div>
          {phases?.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Fase</Label>
              <select value={form.phase} onChange={(e) => setForm((f) => ({ ...f, phase: e.target.value }))} className="w-full border border-brand-border rounded-sm h-8 px-2 mt-0.5 bg-white">
                <option value="">Sin fase</option>
                {phases.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Inicio planificado</Label>
              <Input type="date" value={form.planned_start} onChange={(e) => setForm((f) => ({ ...f, planned_start: e.target.value }))} className="h-8 rounded-sm mt-0.5" data-testid="task-planned-start-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Fin planificado</Label>
              <Input type="date" value={form.planned_end} onChange={(e) => setForm((f) => ({ ...f, planned_end: e.target.value }))} className="h-8 rounded-sm mt-0.5" data-testid="task-planned-end-input" />
            </div>
          </div>
          {/* El campo "Vencimiento" (`due_date`) se fue: era una tercera fecha que nadie
              cargaba y que igual movia el plan. El vencimiento de una tarea es el compromiso
              de su ticket; el fin de la tarea es "Fin planificado". */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Estimacion (h)</Label>
              <Input type="number" step="0.25" min="0" value={form.estimate_hours} onChange={(e) => setForm((f) => ({ ...f, estimate_hours: e.target.value }))} className="h-8 rounded-sm mt-0.5" data-testid="task-estimate-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Dedicacion (%)</Label>
              <Input
                type="number" min="1" max="100" step="5"
                value={form.asignacion_pct}
                onChange={(e) => setForm((f) => ({ ...f, asignacion_pct: e.target.value }))}
                placeholder="100"
                className="h-8 rounded-sm mt-0.5"
                data-testid="task-dedicacion-input"
              />
            </div>
          </div>
          {/* La consecuencia, a la vista. La dedicacion no es un dato mas: **de ella sale el
              largo de la barra**, y sin ver la cuenta el numero se carga a ojo. El esfuerzo
              estimado es un hecho —cuanto trabajo hay—; la duracion es lo que se decide. */}
          <p className="text-[10px] text-zinc-500 -mt-1">{duracionQueResulta}</p>

          {/* **Cambiar un estimado que ya existia pide decir por que.** Aparece solo cuando el
              numero de verdad cambio: la estimacion vieja es la unica evidencia de que el desvio
              existio, y pisarla sin dejar rastro lo borra. */}
          {reestimando && (
            <div className="border border-amber-200 bg-amber-50/50 rounded-sm p-2 space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-amber-900">
                Por que cambia la estimacion
              </Label>
              <p className="text-[10px] text-zinc-600">
                Estaba en <b>{String(estimadoAnterior)} h</b> y queda en <b>{form.estimate_hours} h</b>.
                Queda registrado quien y por que.
              </p>
              <Input
                value={form.motivo_de_reestimacion}
                onChange={(e) => setForm((f) => ({ ...f, motivo_de_reestimacion: e.target.value }))}
                placeholder="El alcance era mayor al relevado"
                className="h-8 rounded-sm text-xs"
                data-testid="task-motivo-reestimacion"
              />
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Asignado</Label>
            <div className="grid grid-cols-3 gap-1 mt-0.5">
              <select value={form.assignee_kind} onChange={(e) => setForm((f) => ({ ...f, assignee_kind: e.target.value, assignee_id: '' }))} className="border border-brand-border rounded-sm h-8 px-1 bg-white text-[10px]">
                <option value="">—</option>
                <option value="human">Persona</option>
                <option value="agent">Agente</option>
              </select>
              {form.assignee_kind === 'human' && (
                <select value={form.assignee_id} onChange={(e) => setForm((f) => ({ ...f, assignee_id: e.target.value }))} className="col-span-2 border border-brand-border rounded-sm h-8 px-1 bg-white text-[10px]">
                  <option value="">Seleccionar...</option>
                  {teamMembers.map((m) => (
                    <option key={m.user_email} value={m.user_email}>{m.full_name || m.user_email}</option>
                  ))}
                </select>
              )}
              {form.assignee_kind === 'agent' && (
                <select value={form.assignee_id} onChange={(e) => setForm((f) => ({ ...f, assignee_id: e.target.value }))} className="col-span-2 border border-brand-border rounded-sm h-8 px-1 bg-white text-[10px]">
                  <option value="">Seleccionar agente...</option>
                  {agents.map((a) => (
                    <option key={a.agent_id} value={a.agent_id}>{a.name || a.agent_id}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Espera a (predecesores)</Label>
            <div className="mt-0.5">
              <DependencyPicker
                tenantId={tenantId}
                capsuleId={capsuleId}
                predecessors={predecessors}
                onChange={setPredecessors}
                excludeTaskId={task?.task_id}
                toKind="task"
                toId={task?.task_id}
                ticketId={ticketId}
                soloDelTicket
                opciones={tareasDelCaso}
                conTipoYEspera
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="text-xs h-8 rounded-sm">Cancelar</Button>
            {/* **Deshabilitado con su motivo, no mudo.** Un boton que se aprieta y no hace nada
                es el defecto que este producto viene sacando: la persona vuelve a apretar y
                concluye que la aplicacion esta rota. */}
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={saving || !form.title.trim() || (reestimando && !form.motivo_de_reestimacion.trim())}
              title={reestimando && !form.motivo_de_reestimacion.trim()
                ? 'Para cambiar una estimacion que ya existia hace falta decir por que'
                : undefined}
              className="text-xs h-8 rounded-sm"
              data-testid="task-save-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
